import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import type { PapiDeviation } from '@wix/da-papi-types';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import DeviationDumbView from '../../../../Dumbo';
import DeviationThumbOverlayDefault from '../../../legos/DeviationThumbOverlayDefault';
import DeviationThumbMiddleContent from '../../../legos/DeviationThumbMiddleContent';
import ThumbOverlayBackground from '../../../legos/ThumbOverlayBackground';
import ThumbLink from '../../../legos/ThumbLink';
import { useDeviationMatureFilter } from '../../../../../utils/hooks/useDeviationMatureFilter';
import { getDeviationOwner } from '../../../../../utils/adoptables';
import { getIsMatureBlock } from '../../../../../Moderation/redux/selectors';
import { OWNER_AVATAR_SIZE } from '../../../../../ShopCard/_partials/ShopCardDumb/ShopCardDumb';
import UserAvatar from '../../../../../User/Avatar/UserAvatar';
import UserLink from '../../../../../User/Link';

import s from './MobileThumbContent.scss';

interface Props {
  deviation: PapiDeviation;
  width?: number;
  height?: number;
  responsive?: boolean;
  className?: string;
  reducedOverlayPadding?: boolean;
  dumboClassName?: string;
}

const MobileThumbContent: React.FC<Props> = ({
  deviation,
  width,
  height,
  responsive,
  className,
  reducedOverlayPadding,
  dumboClassName,
}) => {
  const { t } = useTranslation();
  const { displayReason: blockReason } = useDeviationMatureFilter(deviation);
  const isMatureBlock = getIsMatureBlock(blockReason);
  const owner = getDeviationOwner(deviation);
  return (
    <ThumbLink
      deviation={deviation}
      width={width}
      height={height}
      wrapperClassName={classnames(
        s['root'],
        isMatureBlock && s['mature-block'],
        className
      )}
    >
      <DeviationDumbView
        deviation={deviation}
        width={width}
        height={height}
        responsive={responsive}
        className={dumboClassName}
        videoIconSize={IconSize.GIANT}
        withSeoData
        // We'll rely on the link to inform screen readers
        aria-hidden="true"
      />
      <ThumbOverlayBackground
        deviation={deviation}
        className={s['underlay']}
        showDefaultHover
      />
      <DeviationThumbOverlayDefault
        deviation={deviation}
        width={width}
        height={height}
        className={classnames(
          s['cover'],
          reducedOverlayPadding && s['cover-reduced-padding']
        )}
      >
        {owner && (
          <div className={classnames(
            s['owner-overlay'],
            reducedOverlayPadding && s['owner-reduced-padding']
          )}>
            <div className={s['owner-details']}>
              <UserAvatar user={owner} size={OWNER_AVATAR_SIZE} />
              {t('deviationview.overlay.adoptable.ownedBy')}
              <UserLink user={owner} className={s['owner-link']} />
            </div>
          </div>
        )}
      </DeviationThumbOverlayDefault>
      <DeviationThumbMiddleContent
        deviation={deviation}
        thumbWidth={width}
        thumbHeight={height}
        className={s['middle']}
        isMatureBlock={isMatureBlock}
      />
    </ThumbLink>
  );
};
MobileThumbContent.displayName = 'MobileThumbContent';

export default MobileThumbContent;
